import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { SortDirEnum } from '../../../../common-explain/models/sort';
import { SortDropdownItem } from '../../../../common-explain/components/ex-sort-dropdown/ex-sort-dropdown.component';
import { DbPostSearchSort } from '../../../models/tender-search-body';
import { ResponseDocumentSubtype } from '../../../models/response-document-subtype';
import {
  DocumentProcessingStepEnum,
  isDocumentIndexationPending,
  LibrarySortField,
  LIBRARY_SORT_DROPDOWN_ITEMS,
  ResponseProjectDocument,
} from '../../../models/response-project-document';
import { DuplicateItem, DuplicateResolution } from '../../documents/dedup/dedup.model';
import { ImportHint } from '../../documents/document-import-zone/document-import-zone.component';
import { RejectedFile } from '../../../models/rejected-file';
import { ResponseDialogComponent } from '../../response-dialog/response-dialog.component';
import { LibraryStateService, libraryUploadProcessingStep } from '../../../services/library/library-state.service';

@Component({
  selector: 'app-library-page',
  templateUrl: './library-page.component.html',
  styleUrls: ['./library-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LibraryPageComponent implements OnInit, OnDestroy {
  readonly DocumentProcessingStepEnum = DocumentProcessingStepEnum;
  readonly uploadProcessingStep = libraryUploadProcessingStep;

  importPanelOpen = false;
  /** Collapsed by default while importing — progress bar stays in the header. */
  importProgressExpanded = false;
  emptyHints: ImportHint[] = [];
  emptyFootnote = '';
  private pendingRejected: RejectedFile[] = [];

  private readonly emptyHintDefs: { key: string; highlight?: boolean }[] = [
    { key: 'tenders.library.empty-hints.memoire', highlight: true },
    { key: 'tenders.library.empty-hints.reference' },
    { key: 'tenders.library.empty-hints.equipe' },
    { key: 'tenders.library.empty-hints.presentation' },
    { key: 'tenders.library.empty-hints.doctrine' },
    { key: 'tenders.library.empty-hints.justificatifs' },
    { key: 'tenders.library.empty-hints.fiche' },
  ];

  readonly sortItems: SortDropdownItem[] = LIBRARY_SORT_DROPDOWN_ITEMS.map(item => ({
    label: item.label,
    value: item.value,
    direction: item.direction === 'asc' ? SortDirEnum.ASC : SortDirEnum.DESC,
    unidirectional: true,
  }));

  constructor(
    readonly state: LibraryStateService,
    private modalService: NgbModal,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.emptyHints = this.emptyHintDefs.map(def => ({
      label: this.translate.instant(def.key),
      highlight: def.highlight,
    }));
    this.emptyFootnote = this.translate.instant('tenders.responses.documents.hint-max-size');
    void this.state.init();
  }

  ngOnDestroy(): void {
    this.state.stopIndexationPolling();
  }

  get sortDefault(): DbPostSearchSort {
    const field = this.state.sort();
    return {
      field: field as DbPostSearchSort['field'],
      dir: field === 'name' ? SortDirEnum.ASC : SortDirEnum.DESC,
    };
  }

  onSearch(text: string): void {
    this.state.setQuery(text ?? '');
  }

  onSort(sort: DbPostSearchSort): void {
    const field = String(sort.field) as LibrarySortField;
    if (field === 'name' || field === 'used' || field === 'recent') {
      this.state.setSort(field);
    }
  }

  openImportPanel(): void {
    this.importPanelOpen = true;
  }

  closeImportPanel(): void {
    this.importPanelOpen = false;
  }

  onFilesSelected(files: File[]): void {
    this.importPanelOpen = false;
    const rejected = this.pendingRejected;
    this.pendingRejected = [];
    this.state.startImport(files, rejected);
  }

  toggleImportProgress(): void {
    this.importProgressExpanded = !this.importProgressExpanded;
  }

  uploadSubtypeName(upload: { uncategorized?: boolean; subtypeId?: number | null }): string | null {
    if (upload.uncategorized || upload.subtypeId == null) return null;
    return this.state.subtypeById().get(upload.subtypeId)?.displayedName ?? null;
  }

  onZoneRejected(rejected: RejectedFile[]): void {
    if (!rejected.length) return;
    this.pendingRejected = [...this.pendingRejected, ...rejected];
    // Reject-only drops never emit filesSelected — flush on the next tick.
    setTimeout(() => {
      if (!this.pendingRejected.length) return;
      const onlyRejected = this.pendingRejected;
      this.pendingRejected = [];
      this.importPanelOpen = false;
      this.state.startImport([], onlyRejected);
    });
  }

  onDuplicateResolve(event: { item: DuplicateItem; resolution: DuplicateResolution }): void {
    void this.state.resolveDuplicate(event.item, event.resolution);
  }

  onDelete(doc: ResponseProjectDocument): void {
    const modalRef = this.modalService.open(ResponseDialogComponent, {
      centered: true,
      windowClass: 'project-blocked-modal-dialog',
      backdropClass: 'blur-backdrop',
    });
    modalRef.componentInstance.mode = 'library-document-delete-confirm';
    modalRef.componentInstance.documentName = doc.name;
    modalRef.componentInstance.projects = doc.projects ?? [];
    modalRef.result.then(
      () => void this.state.deleteDocument(doc),
      () => undefined
    );
  }

  onCategorySelected(doc: ResponseProjectDocument, subtype: ResponseDocumentSubtype): void {
    void this.state.updateCategory(doc, subtype);
  }

  onCategoryCustom(doc: ResponseProjectDocument, customName: string): void {
    const autre = this.state.autreSubtype();
    if (!autre) return;
    void this.state.updateCategory(doc, autre, customName);
  }

  categoryValue(doc: ResponseProjectDocument): string | null {
    return this.state.subtypeDisplayName(doc);
  }

  isUncategorized(doc: ResponseProjectDocument): boolean {
    const subtype = this.state.subtypeOf(doc);
    return !subtype || subtype.name === 'undefined';
  }

  rowProcessingStep(doc: ResponseProjectDocument): DocumentProcessingStepEnum | null {
    if (this.isUncategorized(doc)) return null;
    return isDocumentIndexationPending(doc) ? DocumentProcessingStepEnum.INDEXING : null;
  }

  toggleUncategorized(): void {
    this.state.showUncategorized.update(v => !v);
  }
}
